import { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { PH } from '../../components/shared';
import { ModalViewCampaign } from './ModalViewCampaign';
import { ModalNewCampaign } from './ModalNewCampaign';
import { ConfirmModal } from './ConfirmModal';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';

const STATUS_FILTERS = [
  { val: 'all', label: 'All' },
  { val: 'sent', label: 'Sent' },
  { val: 'scheduled', label: 'Scheduled' },
  { val: 'draft', label: 'Drafts' },
  { val: 'cancelled', label: 'Cancelled' },
  { val: 'failed', label: 'Failed' },
];

function humanDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${M[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function humanDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export const PageCampaign = memo(() => {
  const { showToast } = useApp();

  const [profiles, setProfiles] = useState([]);
  const [leads, setLeads] = useState([]);

  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingList, setLoadingList] = useState(true);

  const [viewing, setViewing] = useState(null);     // campaign id (sent → read-only modal)
  const [editing, setEditing] = useState(null);     // full campaign object (draft → wizard)
  const [composing, setComposing] = useState(false);
  const [confirm, setConfirm] = useState(null);     // { title, message, confirmLabel, danger, onConfirm }

  const loadRecipients = useCallback(async () => {
    try {
      const data = await api.getCampaignRecipients();
      setProfiles(data.profiles || []);
      setLeads(data.leads || []);
    } catch (err) {
      showToast(err.message || 'Failed to load recipients', 'error');
    }
  }, [showToast]);

  const loadCampaigns = useCallback(async (pageNum = 1, status = statusFilter) => {
    setLoadingList(true);
    try {
      const res = await api.getCampaigns(pageNum, status);
      setCampaigns(res.data || []);
      setPage(res.current_page || 1);
      setLastPage(res.last_page || 1);
      setTotal(res.total || 0);
    } catch (err) {
      showToast(err.message || 'Failed to load campaigns', 'error');
    } finally {
      setLoadingList(false);
    }
  }, [showToast, statusFilter]);

  useEffect(() => {
    loadRecipients();
    loadCampaigns(1);
  }, []);

  useEffect(() => {
    loadCampaigns(1, statusFilter);
  }, [statusFilter]);

  const handleRowClick = async (c) => {
    if (c.status === 'draft' || c.status === 'scheduled') {
      // Need full detail (with recipients[]) to hydrate the wizard.
      try {
        const detail = await api.getCampaignDetail(c.id);
        setEditing(detail);
      } catch (err) {
        showToast(err.message || 'Failed to load campaign', 'error');
      }
    } else {
      setViewing(c.id);
    }
  };

  const askDelete = (c, e) => {
    e.stopPropagation();
    const isScheduled = c.status === 'scheduled';
    const label = c.name || c.subject || 'this campaign';
    setConfirm({
      title: isScheduled ? 'Remove scheduled campaign?' : 'Delete campaign?',
      message: isScheduled
        ? `"${label}" is currently scheduled. Removing it will cancel the scheduled send and delete the campaign permanently.`
        : `Permanently delete "${label}"? This cannot be undone.`,
      confirmLabel: isScheduled ? 'Cancel & Remove' : 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await api.deleteCampaign(c.id);
          showToast(isScheduled ? 'Scheduled send cancelled and removed.' : 'Campaign deleted.');
          const nextPage = campaigns.length === 1 && page > 1 ? page - 1 : page;
          loadCampaigns(nextPage, statusFilter);
        } catch (err) {
          showToast(err.message || 'Delete failed', 'error');
        }
      },
    });
  };

  const askReschedule = async (c, e) => {
    e.stopPropagation();
    try {
      const detail = await api.getCampaignDetail(c.id);
      setEditing(detail);
    } catch (err) {
      showToast(err.message || 'Failed to load campaign', 'error');
    }
  };

  const counts = useMemo(() => ({
    profiles: profiles.length,
    leads: leads.length,
  }), [profiles, leads]);

  return (
    <div className="pg fi">
      {confirm && (
        <ConfirmModal
          {...confirm}
          onClose={() => setConfirm(null)}
        />
      )}
      {viewing && <ModalViewCampaign id={viewing} onClose={() => setViewing(null)} />}
      {(composing || editing) && (
        <ModalNewCampaign
          profiles={profiles}
          leads={leads}
          existing={editing}
          onClose={() => { setComposing(false); setEditing(null); }}
          onSaved={() => loadCampaigns(page, statusFilter)}
        />
      )}

      <PH
        eyebrow="Campaign"
        title="Email Campaigns"
        sub={`${total} campaign${total !== 1 ? 's' : ''} · ${counts.profiles} profiles · ${counts.leads} leads`}
        action={
          <button className="btn bp" onClick={() => setComposing(true)}>+ New Campaign</button>
        }
      />

      {/* Status filter — segmented control matching the leads page */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{
          display: 'flex', gap: 4,
          background: 'rgba(255,255,255,.7)', padding: 4, borderRadius: 11,
          border: '1px solid rgba(255,255,255,.9)',
        }}>
          {STATUS_FILTERS.map((s) => {
            const active = statusFilter === s.val;
            return (
              <button
                key={s.val}
                onClick={() => setStatusFilter(s.val)}
                style={{
                  padding: '5px 13px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 11.5, fontWeight: active ? 660 : 500, fontFamily: 'var(--f)',
                  background: active ? 'rgba(255,255,255,.96)' : 'transparent',
                  color: active ? '#0f172a' : '#94a3b8',
                  boxShadow: active ? '0 2px 8px rgba(0,0,0,.08)' : 'none',
                  transition: 'all .15s', textTransform: 'capitalize',
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="card-s">
        <div className="ch">
          <div>
            <div className="ct">Email Campaigns</div>
            <div className="cs">{total} total · page {page} of {lastPage}</div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Nr</th>
                <th>Date</th>
                <th>Name / Subject</th>
                <th>Status</th>
                <th>Recipients</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingList && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--t3)' }}>Loading…</td></tr>
              )}
              {!loadingList && campaigns.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--t3)' }}>No campaigns found</td></tr>
              )}
              {!loadingList && campaigns.map((c, i) => (
                <tr key={c.id} onClick={() => handleRowClick(c)} style={{ cursor: 'pointer' }}>
                  <td style={{ color: 'var(--t3)' }}>{(page - 1) * 10 + i + 1}</td>
                  <td>
                    {c.status === 'scheduled' && c.scheduled_at
                      ? <span style={{ color: '#b45309' }}>📅 {humanDateTime(c.scheduled_at)}</span>
                      : c.status === 'sent' && c.sent_at
                        ? humanDate(c.sent_at)
                        : humanDate(c.created_at)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--t1)' }}>{c.name || c.subject}</div>
                    {c.name && c.subject && c.name !== c.subject && (
                      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2 }}>{c.subject}</div>
                    )}
                  </td>
                  <td>
                    <span className={`ec-status-pill ${c.status}`}>{c.status}</span>
                  </td>
                  <td><b style={{ color: 'var(--cyan)' }}>{c.total || 0}</b></td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {c.status === 'scheduled' && (
                        <button
                          className="btn bs"
                          style={{ fontSize: 11.5, padding: '6px 10px' }}
                          onClick={(e) => askReschedule(c, e)}
                          title="Change date / time"
                        >📅 Reschedule</button>
                      )}
                      <button
                        className="btn bd"
                        style={{ fontSize: 11.5, padding: '6px 10px' }}
                        onClick={(e) => askDelete(c, e)}
                        title={c.status === 'scheduled' ? 'Cancel & remove' : 'Delete'}
                      >🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {lastPage > 1 && (
          <div style={{
            padding: '12px 20px',
            display: 'flex', justifyContent: 'center', gap: 6,
            borderTop: '1px solid rgba(0,0,0,.05)',
          }}>
            <button
              className="btn bs"
              disabled={page <= 1 || loadingList}
              onClick={() => loadCampaigns(page - 1, statusFilter)}
              style={{ fontSize: 11.5, padding: '6px 12px', opacity: page <= 1 ? .5 : 1 }}
            >← Prev</button>
            {Array.from({ length: lastPage }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                className={n === page ? 'btn bp' : 'btn bs'}
                style={{ fontSize: 11.5, padding: '6px 12px', minWidth: 34 }}
                onClick={() => n !== page && loadCampaigns(n, statusFilter)}
                disabled={loadingList}
              >{n}</button>
            ))}
            <button
              className="btn bs"
              disabled={page >= lastPage || loadingList}
              onClick={() => loadCampaigns(page + 1, statusFilter)}
              style={{ fontSize: 11.5, padding: '6px 12px', opacity: page >= lastPage ? .5 : 1 }}
            >Next →</button>
          </div>
        )}
      </div>
    </div>
  );
});

